import React, { useState, useEffect, useRef } from 'react';

import { Autocomplete, AutocompleteProps, AutocompleteItem } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompleteClassType from 'hooks/classType/useAutoCompleteClassType';

import SelectorButton from './SelectorButton';

export interface ClassTypeAutoCompleteValue extends AutocompleteItem {
  kiloClassTypeId: string;
  label: string;
}

interface IClassTypeAutoCompleteProps
  extends Omit<
    AutocompleteProps,
    'data' | 'value' | 'onChange' | 'filter' | 'onItemSubmit' | 'rightSection'
  > {
  value: ClassTypeAutoCompleteValue;
  onChange: (item: ClassTypeAutoCompleteValue | null) => void;
  onClearValue?: () => void;
}

const ClassTypeAutoComplete = ({
  value,
  onChange,
  onClearValue,
  ...autocompleteProps
}: IClassTypeAutoCompleteProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [options, setOptions] = useState<AutocompleteItem[]>([]);
  const [search, setSearch] = useState(value.label);
  const [debouncedSearch] = useDebouncedValue(search, 250);

  const { data, isSuccess, isLoading } = useAutoCompleteClassType({
    search: debouncedSearch,
  });

  const handleSearchChange = (query: string): void => {
    setSearch(query);
    onChange(null);
  };
  const handleItemSubmit = (item: ClassTypeAutoCompleteValue): void => {
    setSearch('');
    onChange(item);
  };
  const handleClearSearch = (): void => {
    setSearch('');
  };

  const RightSection = (
    <SelectorButton
      isLoading={isLoading}
      hasText={!!(value.label || search)}
      handleClick={() => {
        handleSearchChange('');

        if (onClearValue) {
          onClearValue();
        }
      }}
      handleFocus={() => inputRef?.current?.focus()}
    />
  );

  useEffect(() => {
    if (isSuccess && data) {
      setOptions(
        data.data.results.map(classType => ({
          ...classType,
          value: classType.kiloClassTypeId,
          label: classType.name,
        })),
      );
    }
  }, [isSuccess, data]);

  return (
    <Autocomplete
      ref={inputRef}
      rightSection={RightSection}
      value={value.label || search}
      onChange={handleSearchChange}
      onItemSubmit={handleItemSubmit}
      onDropdownClose={handleClearSearch}
      filter={() => true}
      limit={options.length}
      maxDropdownHeight={200}
      data={options}
      {...autocompleteProps}
    />
  );
};

export default ClassTypeAutoComplete;
